import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  Download, ArrowRight, CheckCircle2, FileText, LayoutList, Sparkles,
  PenTool, Highlighter, Network, Quote, Star, Loader2,
} from "lucide-react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { API_BASE } from "@/services/api";
import SEO from "@/components/SEO";

const FEATURE_ICONS = [Star, LayoutList, Sparkles, Network, Highlighter, PenTool];

// Read Instagram/UTM attribution off the URL once.
const getAttribution = () => {
  const p = new URLSearchParams(window.location.search);
  return {
    source: p.get("source") || "instagram",
    utm_source: p.get("utm_source") || p.get("source") || null,
    utm_medium: p.get("utm_medium") || null,
    utm_campaign: p.get("utm_campaign") || null,
    campaign_name: p.get("campaign_name") || p.get("campaign") || p.get("utm_campaign") || null,
    keyword: p.get("keyword") || p.get("kw") || null,
  };
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const Section = ({ children, className = "" }) => (
  <section className={`w-full max-w-7xl mx-auto px-6 md:px-12 ${className}`}>{children}</section>
);

export default function FreeAnswerPage() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [attribution] = useState(getAttribution);

  useEffect(() => {
    axios.get(`${API_BASE}/free-answer/config`).then((r) => setCfg(r.data)).catch(() => setCfg(null));
    // fire page_view once
    axios.post(`${API_BASE}/free-answer/track`, { event_type: "page_view", ...attribution })
      .catch(() => {});
  }, [attribution]);

  const track = useCallback((event_type) => {
    axios.post(`${API_BASE}/free-answer/track`, { event_type, ...attribution }).catch(() => {});
  }, [attribution]);

  const downloadUrl = () => {
    const q = new URLSearchParams(Object.fromEntries(
      Object.entries(attribution).filter(([, v]) => v)));
    return `${API_BASE}/free-answer/download?${q.toString()}`;
  };

  const onDownload = () => {
    // The endpoint returns Content-Disposition: attachment. Triggering it via a
    // hidden <a download> downloads the file in-place — no leftover blank tab
    // (window.open would leave an about:blank tab behind for attachment responses).
    const a = document.createElement("a");
    a.href = downloadUrl();
    a.setAttribute("download", "");
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onExplore = () => { track("explore_click"); navigate("/"); };
  const onStartPreparing = () => { track("install_click"); navigate("/auth"); };

  if (!cfg) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center" data-testid="free-answer-loading">
        <Loader2 className="w-8 h-8 text-[#f97316] animate-spin" />
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Free UPSC Topper Sample Answer PDF | Clarias",
    description: "Download a free UPSC topper sample answer and learn answer writing techniques with Clarias.",
    url: "https://clarias.in/free-answer",
    isPartOf: { "@type": "WebSite", name: "Clarias", url: "https://clarias.in" },
    about: { "@type": "EducationalOccupationalProgram", name: "UPSC Civil Services Preparation" },
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white antialiased" data-testid="free-answer-page">
      <SEO
        title="Free UPSC Topper Sample Answer PDF | Clarias"
        description="Download a free UPSC topper sample answer and learn answer writing techniques with Clarias."
        canonical="/free-answer"
        ogType="website"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ------------------------------------------------------------- Hero */}
      <div className="relative overflow-hidden min-h-[92vh] flex items-center">
        <img
          src={cfg.hero_image_url}
          alt=""
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1a]/80 via-[#0a0f1a]/90 to-[#0a0f1a]" />
        <Section className="relative z-10 text-center py-24">
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="text-xs font-bold uppercase tracking-[0.25em] text-[#f97316] mb-5">
            Clarias · Clarity over Chaos
          </motion.p>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl mx-auto leading-tight"
            data-testid="hero-title">
            {cfg.hero_title}
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            {cfg.hero_subtitle}
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={onDownload} data-testid="hero-download-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#f97316] hover:bg-[#ea580c] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-orange-900/30 transition-colors">
              <Download className="w-5 h-5" />
              {cfg.primary_cta}
            </button>
            <button onClick={onExplore} data-testid="hero-explore-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/5 px-8 py-4 text-base font-semibold text-white transition-colors">
              {cfg.secondary_cta}
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
          {cfg.resource && (
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={4}
              className="mt-6 text-sm text-slate-400 inline-flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#f97316]" />
              {cfg.resource.title} · Free PDF
            </motion.p>
          )}
        </Section>
      </div>

      {/* --------------------------------------------------------- Features */}
      <Section className="py-24 md:py-28">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="font-heading text-3xl sm:text-4xl tracking-tight text-center mb-4">
          What makes a topper's answer different
        </motion.h2>
        <p className="text-slate-400 text-center max-w-2xl mx-auto mb-14">
          Every element the examiner rewards, decoded in one sample answer.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {cfg.features.map((f, i) => {
            const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
            return (
              <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="show"
                viewport={{ once: true }} data-testid={`feature-card-${i + 1}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-7 hover:border-[#f97316]/40 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[#f97316]/15 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-[#f97316]" />
                </div>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> {f.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* ------------------------------------------------------------ About */}
      <div className="bg-white/[0.02] border-y border-white/10">
        <Section className="py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#f97316] mb-4">About Clarias</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight mb-6">
              Prepare smarter, not harder.
            </h2>
            <p className="text-slate-300 leading-relaxed mb-8">{cfg.about_text}</p>
            <button onClick={onStartPreparing} data-testid="about-cta-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#f97316] hover:bg-[#ea580c] px-7 py-3.5 font-semibold transition-colors">
              {cfg.about_cta} <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="grid grid-cols-2 gap-4">
            {[
              ["Expert Evaluation", "Examiner-grade rubric feedback"],
              ["PYQ Analysis", "Decode past-paper patterns"],
              ["Answer Tools", "Structure, keywords, value-add"],
              ["Daily Intelligence", "Mains-ready current affairs"],
            ].map(([t, d], i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                <p className="font-semibold text-sm mb-1">{t}</p>
                <p className="text-xs text-slate-400">{d}</p>
              </div>
            ))}
          </motion.div>
        </Section>
      </div>

      {/* ------------------------------------------------------ Testimonials */}
      <Section className="py-24">
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-center mb-14">
          Loved by aspirants
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cfg.testimonials.map((t, i) => (
            <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="show"
              viewport={{ once: true }} data-testid={`testimonial-card-${i + 1}`}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
              <Quote className="w-8 h-8 text-[#f97316]/50 mb-4" />
              <p className="text-slate-200 leading-relaxed mb-6">"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#f97316]/15 border border-[#f97316]/30 flex items-center justify-center text-[#f97316] font-semibold text-sm shrink-0">
                  {(t.name || "?").trim().charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* -------------------------------------------------------------- FAQ */}
      <div className="bg-white/[0.02] border-t border-white/10">
        <Section className="py-24 max-w-3xl">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-center mb-12">
            Frequently asked questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {cfg.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                <AccordionTrigger data-testid={`faq-trigger-${i + 1}`}
                  className="text-base font-medium text-white hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-14">
            <button onClick={onDownload} data-testid="faq-download-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#f97316] hover:bg-[#ea580c] px-8 py-4 font-semibold transition-colors">
              <Download className="w-5 h-5" /> {cfg.primary_cta}
            </button>
          </div>
        </Section>
      </div>

      <footer className="text-center py-10 text-xs text-slate-500 border-t border-white/10">
        © {new Date().getFullYear()} Clarias · Clarity over Chaos
      </footer>

      {/* ------------------------------------------------ Sticky mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-[#0a0f1a]/95 backdrop-blur-md border-t border-white/10 md:hidden">
        <button onClick={onDownload} data-testid="mobile-sticky-download-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#f97316] hover:bg-[#ea580c] px-6 py-3.5 font-semibold transition-colors">
          <Download className="w-5 h-5" /> {cfg.primary_cta}
        </button>
      </div>
      <div className="h-20 md:hidden" />
    </div>
  );
}
